/**
 * Workspace human agents via tenant-user-dashboard BFF.
 * Requires active workspace (`X-Workspace-Id` via {@link import("@/lib/tenantApi").setTenantApiWorkspaceId}).
 */

import { tenantApiFetch, tenantDashboardPath } from "@/lib/tenantApi";

/**
 * @param {{ limit?: number, offset?: number, scope?: "workspace" | "tenant" }} [params]
 */
export async function listWorkspaceAgents(params = {}) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  if (params.scope) q.set("scope", String(params.scope));
  const suffix = `/directory/agents${q.size ? `?${q}` : ""}`;
  return tenantApiFetch(tenantDashboardPath(suffix));
}

/**
 * List unconsumed agent invitations for the active workspace (or entire tenant).
 * Returns `{ invitations: Array<{ email, role, status, expiresAt, createdAt, invitedByEmail, workspaceId? }> }`.
 * @param {{ scope?: "workspace" | "tenant" }} [params]
 */
export async function listPendingInvitations(params = {}) {
  const q = new URLSearchParams();
  if (params.scope) q.set("scope", String(params.scope));
  const suffix = `/directory/agents/pending-invitations${q.size ? `?${q}` : ""}`;
  return tenantApiFetch(tenantDashboardPath(suffix));
}

/**
 * List assignable roles for the active workspace.
 * Returns `{ roles: Array<{ key, label, isSystem }> }`.
 */
export async function listWorkspaceRoles() {
  return tenantApiFetch(tenantDashboardPath("/directory/roles"));
}

/**
 * Send a workspace-scoped agent invitation email.
 * @param {{ email: string, role: string }} params
 */
export async function sendAgentInvitation({ email, role }) {
  return tenantApiFetch(tenantDashboardPath("/workspaces/agent-invitations"), {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
}

/**
 * Re-send an invitation for an expired invite or a revoked agent.
 * @param {{ email: string, role?: string, workspaceId?: string | null }} params
 */
export async function reinviteAgentInvitation({ email, role = "chat", workspaceId = null }) {
  const ws = workspaceId ? String(workspaceId).trim() : "";
  return tenantApiFetch(tenantDashboardPath("/workspaces/agent-invitations/reinvite"), {
    method: "POST",
    body: JSON.stringify(
      ws ? { email: email.trim().toLowerCase(), role, workspaceId: ws } : { email: email.trim().toLowerCase(), role }
    ),
  });
}

/**
 * Revoke a pending agent invitation.
 * @param {{ email: string, scope?: "workspace" | "tenant" }} params
 */
export async function revokeAgentInvitation({ email, scope = "workspace" }) {
  return tenantApiFetch(tenantDashboardPath("/workspaces/agent-invitations/revoke"), {
    method: "POST",
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      scope,
    }),
  });
}

/**
 * Set an agent's roles in one workspace (replaces all prior roles there only).
 * Each role must exist in that workspace's role catalog. `workspaceId` defaults to
 * the active workspace — pass it explicitly from the tenant-wide listing, where a
 * row may belong to a workspace other than the active one.
 * @param {{ email: string, roles: string[], workspaceId?: string | null }} params
 */
export async function changeAgentRoles({ email, roles, workspaceId = null }) {
  const ws = workspaceId ? String(workspaceId).trim() : "";
  return tenantApiFetch(tenantDashboardPath("/directory/agents/role"), {
    method: "PATCH",
    body: JSON.stringify(ws ? { email, roles, workspaceId: ws } : { email, roles }),
  });
}

/**
 * Fully disable 2FA for an agent (next login is unchallenged until they enable it again).
 * @param {{ email: string }} params
 */
export async function resetAgentTwoFa({ email }) {
  return tenantApiFetch(tenantDashboardPath("/directory/agents/reset-2fa"), {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/**
 * Revoke access for an accepted agent.
 * @param {{ email: string, scope: "workspace" | "tenant" }} params
 */
export async function revokeAgentAccess({ email, scope }) {
  return tenantApiFetch(tenantDashboardPath("/directory/agents/revoke-access"), {
    method: "POST",
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      scope,
    }),
  });
}
